import { useState } from 'react';

import { WrappedControlProps } from 'app/components/Controls/WrappedControlProps';
import { clearAnonymization } from 'app/shared/helpers/clearAnonymization';
import { FormsEnum } from 'app/shared/enums';
import {
  containsValidVinCharacters as containsValidCarVinCharacters,
  isVinValid as isCarVinValid,
} from 'app/validators/validateCarVin';
import {
  containsValidVinCharacters as containsValidMotorcycleVinCharacters,
  isVinValid as isMotorcycleVinValid,
} from 'app/validators/validateMotorcycleVin';

const VIN_MAX_LENGTH = 17;

export function useVinControl({
  input,
  onClick,
  onControlBlur,
  onValueChange,
  isMotor,
  formName,
}: WrappedControlProps & { isMotor?: boolean }) {
  const [focused, setFocused] = useState<boolean>(false);

  const validVinCharactersValidator = isMotor
    ? containsValidMotorcycleVinCharacters
    : containsValidCarVinCharacters;

  const vinValidator = isMotor ? isMotorcycleVinValid : isCarVinValid;

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    handleFocus(e.target.value);
    setFocused(true);
  };

  const isMotorcycleCheckoutVin =
    isMotor && formName === FormsEnum.CheckoutForm;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = event.target;

    if (
      value &&
      (value.length > VIN_MAX_LENGTH || !validVinCharactersValidator(value))
    ) {
      return;
    }

    input.onChange(value);
    if (onValueChange) {
      onValueChange(value, false, false, !isMotorcycleCheckoutVin);
    }
  };

  const handleFocus = (value: string) => {
    const clearedValue = clearAnonymization(value);

    if (clearedValue !== value) {
      input.onChange(clearedValue);
      if (onValueChange) {
        onValueChange(clearedValue, false, false);
      }
    }
  };

  const handleBlur = () => {
    const formattedValue = input.value.toUpperCase();
    const shouldPatch = vinValidator(formattedValue) || !formattedValue;

    setFocused(false);
    input.onBlur(formattedValue);

    if (onControlBlur) {
      setTimeout(() => {
        onControlBlur(shouldPatch, true, isMotorcycleCheckoutVin);
      }, 0);
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return {
    focused,
    maxLength: VIN_MAX_LENGTH,
    handleBlur,
    handleChange,
    handleClick,
    onFocus,
  };
}
